import { Static, Type } from '@sinclair/typebox'
import { Nullable } from '../common/base-model'
import { PopulatedFlow } from '../flows'
import { Action } from '../flows/actions/action'
import { Connection } from '../flows/connections/connection'
import { Trigger } from '../flows/triggers/trigger'
import { AiBuilderResponseMessage } from './ai-builder-response-message'
import { AiBuilderText } from './ai-builder-text'
import { AiBuilderCard } from './cards'
import { AI_BUILDER_PROMPT_HARD_CAP } from './payload-limits'
import { SuggestedAction } from './turn-input'

export * from './ai-builder-text'
export * from './cards'
export * from './payload-limits'
export * from './turn-input'

export const AiBuilderMessage = Type.Object({
    role: Type.Union([Type.Literal('user'), Type.Literal('assistant')]),
    content: Type.String({ maxLength: AI_BUILDER_PROMPT_HARD_CAP }),
})

export type AiBuilderMessage = Static<typeof AiBuilderMessage>

export const NavigateDirective = Type.Object({
    stepName: Type.String(),
    propToHighlight: Type.Optional(Type.String()),
    action: Type.Optional(Type.Literal('OPEN_AUTH')),
    buttonLabel: Type.Optional(Type.String()),
})

export type NavigateDirective = Static<typeof NavigateDirective>


/**
 * The one list of step actions a pending question can be about — both the
 * wire's `PendingQuestionSubjectDisplay` and the server's own internal
 * `PendingQuestion` (`core/types.ts`) read this type, so the two never drift
 * out of step with each other.
 */
export type PendingQuestionStepAction =
    'test' | 'fix_failed_test' | 'complete_router_condition' | 'review_write'

/**
 * What the pending question is ABOUT, typed so the chip never shows a
 * technical value (a `step_action` enum member, a piece package name) —
 * the front picks its own Tolgee key per `kind` (and per `action` for
 * `step_action`), it never renders a server-built sentence.
 */
export const PendingQuestionSubjectDisplay = Type.Union([
    Type.Object({ kind: Type.Literal('field'), label: Type.String() }),
    Type.Object({ kind: Type.Literal('connection'), pieceDisplayName: Type.String() }),
    Type.Object({
        kind: Type.Literal('step_action'),
        action: Type.Union([
            Type.Literal('test'),
            Type.Literal('fix_failed_test'),
            Type.Literal('complete_router_condition'),
            Type.Literal('review_write'),
        ]),
    }),
    Type.Object({ kind: Type.Literal('framing'), question: Type.String() }),
])

export type PendingQuestionSubjectDisplay = Static<typeof PendingQuestionSubjectDisplay>

/** What the server is now waiting for the user to answer — display only (the "Réponse à" chip) */
export const PendingQuestionDisplay = Type.Object({
    id: Type.String(),
    stepDisplayName: Nullable(Type.String()),
    subject: PendingQuestionSubjectDisplay,
})

export type PendingQuestionDisplay = Static<typeof PendingQuestionDisplay>

export const AskAiBuilderResponse = Type.Object({
    /** Echo of GenerateFlowRequest.requestId */
    requestId: Type.Optional(Type.String()),
    /** Null when the turn was refused before touching the flow (busy, quota, too long, invalid payload) */
    flow: Type.Union([PopulatedFlow, Type.Null()]),
    message: Type.Optional(AiBuilderResponseMessage),
    waitingForReply: Type.Optional(Type.Boolean()),
    navigateDirectives: Type.Optional(Type.Array(NavigateDirective)),
    suggestedActions: Type.Optional(Type.Array(SuggestedAction)),
    /** At most one interactive card per turn */
    card: Type.Optional(AiBuilderCard),
    /**
   * The wizard walked every step and the flow is complete — the same signal
   * that produced the summary and flipped the steps valid. Improvement
   * suggestions are no proxy for it: a turn ending on a skipped test has none.
   */
    flowReady: Type.Optional(Type.Boolean()),
    /** Display only: the "Réponse à" chip. Absent when nothing is pending. */
    pendingQuestion: Type.Optional(PendingQuestionDisplay),
})

export type AskAiBuilderResponse = Static<typeof AskAiBuilderResponse>

// Mid-turn events carry their flow AND their turn: the socket is one per tab,
// a turn keeps emitting after the tab moved to another flow's chat (flowId),
// and a second turn on the SAME flow — another tab, or one that outlived its
// own timeout — must not have its events mistaken for the current one
// (requestId).

/**
 * One mid-turn progress line (AI_BUILDER_NARRATION event): what the turn just
 * did on its own. `line` is always a Tolgee key in practice (every current
 * producer narrates through `AiBuilderMessageKey`), but is typed as the full
 * `AiBuilderText` union — the wire contract, not an internal assumption.
 */
export const AiBuilderNarration = Type.Object({
    flowId: Type.String(),
    /** Echo of GenerateFlowRequest.requestId — absent for an old client that never sent one */
    requestId: Type.Optional(Type.String()),
    line: AiBuilderText,
})

export type AiBuilderNarration = Static<typeof AiBuilderNarration>

/**
 * The loader line while a tool runs (AI_BUILDER_PROGRESS event). `message` is
 * a Tolgee key, translated on the front.
 */
export const AiBuilderProgress = Type.Object({
    flowId: Type.String(),
    requestId: Type.Optional(Type.String()),
    message: Type.String(),
})

export type AiBuilderProgress = Static<typeof AiBuilderProgress>

export const AiBuilderFlowPartialUpdate = Type.Object({
    flowId: Type.String(),
    requestId: Type.Optional(Type.String()),
    flowElements: Type.Object({
        triggers: Type.Array(Trigger),
        actions: Type.Array(Action),
        connections: Type.Array(Connection),
    }),
})

export type AiBuilderFlowPartialUpdate = Static<
  typeof AiBuilderFlowPartialUpdate
>

export const DynamicPropertiesValues = Type.Record(Type.String(), Type.Any())

export type DynamicPropertiesValues = Static<typeof DynamicPropertiesValues>

/** Hard bounds on an `about` reference — a hint the server re-verifies, never trusts */
const AI_BUILDER_ABOUT_RUN_ID_MAX_LENGTH = 50
const AI_BUILDER_ABOUT_STEP_NAME_MAX_LENGTH = 100

/**
 * What the conversation was opened about: a specific run or step, sent once
 * by the front (the "Understand this error" entry point) so a diagnosis never has to
 * guess which run or step the user means. A hint, not a fact: the server
 * re-fetches and re-checks ownership before trusting either id.
 */
export const AiBuilderAbout = Type.Union([
    Type.Object({
        type: Type.Literal('run'),
        runId: Type.String({ maxLength: AI_BUILDER_ABOUT_RUN_ID_MAX_LENGTH }),
        stepName: Type.Optional(Type.String({ maxLength: AI_BUILDER_ABOUT_STEP_NAME_MAX_LENGTH })),
    }),
    Type.Object({
        type: Type.Literal('step'),
        stepName: Type.String({ maxLength: AI_BUILDER_ABOUT_STEP_NAME_MAX_LENGTH }),
    }),
])

export type AiBuilderAbout = Static<typeof AiBuilderAbout>

